import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  toast,
  Typography,
  Stack
} from "@altimateai/lego";
import { getIconByDatastoreType } from "../../DatastoreImage";
import { DatastoreTypeEnum, DbtCloudValidationResponse } from "../../../types";
import { validateDbtCloudToken } from "@/helpers/apis";
import { useMutation } from "@tanstack/react-query";
import { ConnectionProps } from "./types";
import ProjectsEnvironmentsModal from "./ProjectsEnvironmentsModal";
import ValidationSuccessBanner from "./ValidationSuccessBanner";
import TokenInfoBox from "./TokenInfoBox";
import ConnectionFormActions from "./ConnectionFormActions";
import {
  connectionSchema,
  ConnectionFormValues,
  DEFAULT_API_BASE_URL,
} from "./dbtCloudConnectionSchema";

export type { DbtCloudConnectionData } from "./dbtCloudConnectionSchema";

const DbtCloudConnection = ({
  typeData,
  handleBack,
  handleConnectionSubmit,
}: ConnectionProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Check if validation was already done (user navigated back from step 3)
  const previousValidation = typeData.connection?.validationResult as
    | DbtCloudValidationResponse
    | undefined;
  const wasAlreadyValidated = Boolean(previousValidation?.valid);

  const form = useForm<ConnectionFormValues>({
    resolver: zodResolver(connectionSchema),
    defaultValues: {
      account_id: (typeData.connection?.account_id as string) || "",
      api_token: (typeData.connection?.api_token as string) || "",
      api_base_url: (typeData.connection?.api_base_url as string) || DEFAULT_API_BASE_URL,
    },
    mode: "onChange",
  });

  const {
    mutate: validateToken,
    isPending: isValidating,
    isSuccess: isMutationSuccess,
    data: mutationResult,
  } = useMutation({
    mutationFn: async (data: ConnectionFormValues) => {
      const response = await validateDbtCloudToken({
        api_token: data.api_token,
        account_id: data.account_id,
        api_base_url: data.api_base_url,
      });
      if (!response?.valid) {
        throw new Error(response?.message || "Invalid credentials");
      }
      return response;
    },
    onSuccess: response => {
      const hasDiscoveryData = response?.projects_discovered != null;
      toast({
        title: "Connection validated",
        description: hasDiscoveryData ? (
          <span>
            Successfully connected to &quot;{response?.account_name || response?.account_id}&quot;.{" "}
            <strong>
              {response.projects_discovered} Project{response.projects_discovered !== 1 ? "s" : ""}
            </strong>{" "}
            and{" "}
            <strong>
              {response.environments_discovered} Environment
              {response.environments_discovered !== 1 ? "s" : ""}
            </strong>{" "}
            added. Click{" "}
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="al-underline al-font-medium"
            >
              here
            </button>{" "}
            to view details.
          </span>
        ) : (
          `Successfully connected to account "${response?.account_name || response?.account_id}".`
        ),
      });
    },
    onError: (err: Error) => {
      toast({
        variant: "destructive",
        title: "Connection test failed",
        description: err.message || "Unable to validate credentials",
      });
    },
  });

  // Use either previous validation or current mutation result
  const isValidationSuccess = wasAlreadyValidated || isMutationSuccess;
  const validationResult = mutationResult || previousValidation;

  const handleTestConnection = async () => {
    const isValid = await form.trigger(["api_token", "account_id", "api_base_url"]);
    if (isValid) {
      const values = form.getValues();
      validateToken(values);
    }
  };

  const handleSubmit = (values: ConnectionFormValues) => {
    if (!isValidationSuccess) {
      toast({
        variant: "destructive",
        title: "Please test connection first",
        description: "You must validate your credentials before proceeding.",
      });
      return;
    }

    handleConnectionSubmit?.({
      ...values,
      validationResult: validationResult ?? null,
    });
  };

  const icon = getIconByDatastoreType({
    datastoreType: DatastoreTypeEnum.DBT_CLOUD,
    className: "al-w-10 al-h-10 al-mr-3",
  });

  return (
    <Card>
      <CardContent>
        <Stack direction="row" align="center" className="al-mb-6 al-pt-6">
          {icon}
          <div>
            <Typography variant="section-heading">dbt Cloud Connection</Typography>
            <Typography size="sm" className="al-text-muted-foreground">
              Connect your dbt Cloud account to sync artifacts
            </Typography>
          </div>
        </Stack>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="al-space-y-4">
            <TokenInfoBox />

            <Stack direction="row" gap="lg">
              <FormField
                control={form.control}
                name="api_token"
                render={({ field }) => (
                  <FormItem className="al-flex-1">
                    <FormLabel>Add Service Account Token</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Enter token here"
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="account_id"
                render={({ field }) => (
                  <FormItem className="al-flex-1">
                    <FormLabel>Add Account ID</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter account ID" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                    <Typography size="xs" className="al-text-muted-foreground">
                      can be retrieved in this URL:{" "}
                      <a
                        href="https://cloud.getdbt.com/next/settings/accounts"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="al-text-primary al-underline"
                      >
                        {"https://cloud.getdbt.com/next/settings/accounts/{{account_id}}"}
                      </a>
                    </Typography>
                  </FormItem>
                )}
              />
            </Stack>

            <FormField
              control={form.control}
              name="api_base_url"
              render={({ field }) => (
                <FormItem className="al-max-w-[565px]">
                  <FormLabel>Add Custom URL or Access URL in dbt (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter custom URL" {...field} />
                  </FormControl>
                  <FormMessage />
                  <Typography size="xs" className="al-text-muted-foreground">
                    can be retrieved in this guide:{" "}
                    <a
                      href="https://cloud.getdbt.com/guide"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="al-text-primary al-underline"
                    >
                      https://cloud.getdbt.com/guide
                    </a>
                  </Typography>
                </FormItem>
              )}
            />

            {isValidationSuccess && validationResult?.projects_discovered != null && (
              <ValidationSuccessBanner
                projectsCount={validationResult.projects_discovered ?? 0}
                environmentsCount={validationResult.environments_discovered ?? 0}
                onViewDetails={() => setIsModalOpen(true)}
              />
            )}

            <ConnectionFormActions
              onTestConnection={handleTestConnection}
              onBack={handleBack}
              isValidating={isValidating}
              isFormValid={form.formState.isValid}
              isValidationSuccess={isValidationSuccess}
            />
          </form>
        </Form>
      </CardContent>

      <ProjectsEnvironmentsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        projects={validationResult?.projects ?? []}
      />
    </Card>
  );
};

export default DbtCloudConnection;
